"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getMessageText = exports.defaultProps = exports.default = exports.Tag = void 0;
require("./Tag.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _useTimerMessageGenerator = require("../../util/hooks/useTimerMessageGenerator");
var _renderObject = require("../../util/renderObject/renderObject");
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * Tag Component
 * @method
 * @param {object} props - React properties passed from composition
 * @returns Tag
 */

const Tag = props => {
  const {
    backgroundColor,
    boldText,
    endTime,
    externalAction,
    icon,
    iconColor,
    message,
    size,
    startTime,
    style,
    textColor,
    theme = defaultProps.theme,
    timerMeta,
    timerType,
    variant = defaultProps.variant,
    ...other
  } = props;
  const formattedUrgencyMessage = (0, _useTimerMessageGenerator.useTimerMessageGenerator)({
    endTime,
    startTime,
    timerMeta,
    timerType
  });

  // the only tags that accept a background color are icon, custom, urgency, and default
  // they currently only accept white
  const isValueTag = variant === 'value';

  // checks if variant is custom for extra transparent setting
  const isCustom = variant === 'custom';

  // the only tags that support size are custom, urgency, icon, value and default
  // custom tags and urgency tags can have - default, compact and large size
  // icon tags and default tags can have - default and compact size
  // value tags can have - default and large size
  // (the only sizes accepted is large, default, compact)
  const canHaveSize = ['custom', 'urgency', 'icon', 'value', 'default'].includes(variant);

  // the only tag that doesn't support an icon is utilityCompact
  const canHaveIcon = variant !== 'utilityCompact';

  // use colors from DXL when they are available
  const useExternalColors = !!textColor || !!backgroundColor;

  // We have to manually set the transparent background because DXL sends it as null
  const isTransparentBackground = !backgroundColor || backgroundColor === 'transparent';

  // checks if a timer is configured
  const hasTimer = timerType !== undefined;
  let tagStyle = {};
  let derivedBackgroundColor = backgroundColor;
  if (useExternalColors) {
    // format the background and text colors when we receive either one from DXL.
    if (isTransparentBackground) {
      derivedBackgroundColor = 'transparent';
    }
    const bgColorToken = useExternalColors && backgroundColor ? [`--pal-colors-${theme}-${derivedBackgroundColor}`] : '';
    const textColorToken = useExternalColors && textColor ? [`--pal-colors-${theme}-${textColor}`] : '';
    tagStyle = {
      '--tag-background-color': `var( ${bgColorToken} )`,
      '--tag-text-color': `var( ${textColorToken} )`
    };
  }
  const componentClassName = (0, _classnames.default)('pal-c-Tag', {
    [`pal-c-Tag__${variant}`]: variant,
    [`pal-c-Tag__${variant}--${style}`]: variant && !useExternalColors && style,
    [`pal-c-Tag__size--${size}`]: size && canHaveSize,
    [`pal-c-Tag--hasTimer`]: hasTimer,
    [`pal-c-Tag--isTransparent`]: isTransparentBackground && isCustom,
    [`pal-c-Tag--withIcon`]: icon && canHaveIcon,
    [`pal-c-Tag--customBackgroundColor`]: !isValueTag && backgroundColor && useExternalColors,
    [`pal-c-Tag--customTextColor`]: useExternalColors && textColor
  });
  const messageClassName = (0, _classnames.default)('pal-c-Tag__message', {
    [`pal-c-Tag--customBackgroundColor`]: isValueTag && backgroundColor
  });

  // Method to set the message text based on the type of message
  const messageText = getMessageText({
    boldText,
    formattedUrgencyMessage,
    message,
    timerType
  });
  const showMessage = boldText || messageText;

  // Only show actions and iconColor for icon variant
  const showDisabledIcon = ['icon'].includes(variant) && iconColor === 'grayscale';
  const showAction = !!externalAction && ['icon', 'custom'].includes(variant);
  return /*#__PURE__*/_react.default.createElement("p", _extends({
    className: componentClassName,
    style: useExternalColors ? tagStyle : undefined
  }, other), canHaveIcon && icon && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    className: "pal-c-Tag__icon-component",
    name: icon,
    disabled: showDisabledIcon
  }), (showMessage || showAction) && /*#__PURE__*/_react.default.createElement("span", {
    className: messageClassName
  }, boldText && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Tag__messageTitle"
  }, boldText), messageText && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Tag__messageText"
  }, messageText), showAction && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Tag__action"
  }, (0, _renderObject.renderObject)(externalAction))));
};

/**
 * Gets the messageText for the Tag based on what information it has
 * @method
 * @param {Object} props - React properties passed from composition
 * @returns {string} getMessageText
 */
exports.Tag = Tag;
const getMessageText = data => {
  const {
    formattedUrgencyMessage,
    message,
    timerType
  } = data;

  // Checks to see what kind of message we have for the tag
  const hasMessageAndTimer = message !== undefined && timerType !== undefined;
  const onlyMessage = message !== undefined && timerType === undefined;
  const onlyTimer = message === undefined && timerType !== undefined;
  let newMessageText = undefined;
  if (hasMessageAndTimer) {
    newMessageText = [`${message} ${formattedUrgencyMessage}`];
  }
  if (onlyMessage) {
    newMessageText = [`${message}`];
  }
  if (onlyTimer) {
    newMessageText = [`${formattedUrgencyMessage}`];
  }
  return newMessageText;
};

/**
 * Property type definitions
 * @type {object}
 * @property {string} backgroundColor - DXL only setting for background color
 * @property {string} boldText - bold text as title for compact utility tags
 * @property {string} endTime - end date and time for urgency tag (ISO-8601 representation)[ endTime = "2023-03-22T08:00:00.000Z"]
 * @property {object} externalAction - the externalAction object for icon tag
 * @property {string} icon - name of the image to set infront of message
 * @property {string} iconColor - name of the icon color to set (default or grayscale)
 * @property {string} message - text passed as string
 * @property {string} size - size of urgency or icon tags (accepts large, default, or compact)
 * @property {string} startTime - start date and time for urgency tag [ startTime = "2023-03-22T08:00:00.000Z"]
 * @property {string} style - sets the style of the tag
 * @property {string} textColor - DXL only setting for text color
 * @property {object} timerMeta - string token object for urgency tag message
 * @property {string} timerType - type of urgency timer [3+ days, 2 days, 1 day, Hours, Extended, Upcoming, Timer]
 * @property {string} variant - the variant of tag [custom, default, icon, utility, utilityCompact, urgency, value]
 */
exports.getMessageText = getMessageText;
const propTypes = {
  /** DXL only setting for background color */
  backgroundColor: _propTypes.default.string,
  /** bold text as title */
  boldText: _propTypes.default.string,
  /** end date and time for urgency tag (ISO-8601 representation) [ endTime = "2023-03-28T08:00:00.000Z"] */
  endTime: _propTypes.default.string,
  /** Sets the primary action from an external source that contains business logic for icon tag */
  externalAction: _propTypes.default.object,
  /** name of the image to set infront of message */
  icon: _propTypes.default.string,
  /** icon color */
  iconColor: _propTypes.default.oneOf(['default', 'grayscale']),
  /** text passed as string */
  message: _propTypes.default.string,
  /** size of tag (accepts compact, default, or large) */
  size: _propTypes.default.oneOf(['compact', 'default', 'large']),
  /** start date and time for urgency tag [ startTime = "2023-03-22T08:00:00.000Z"] */
  startTime: _propTypes.default.string,
  /**
   * Determines style of tag
   * - **default:** The default tag style
   * - **white:** The white tag style
   * - **transparent:** The transparent tag style
   * - **validation:** The validation tag style
   * - **notification:** The notification tag style
   * - **warning:** The warning tag style
   * - **error:** The error tag style
   */
  style: _propTypes.default.oneOf(['default', 'white', 'transparent', 'validation', 'notification', 'warning', 'error']),
  /** DXL only setting for text color */
  textColor: _propTypes.default.string,
  /** string token object for urgency tag message */
  timerMeta: _propTypes.default.object,
  /**
   * Determines the type of urgency timer
   * - **3+ days:** The 3+ days style
   * - **2 days:** The 2 days style
   * - **1 day:** The 1 day style
   * - **Hours:** The Hours style
   * - **Extended:** The Extended style
   * - **Upcoming:** The Upcoming style
   * - **Timer:** The Timer style
   */
  timerType: _propTypes.default.oneOf(['3+ days', '2 days', '1 day', 'Hours', 'Extended', 'Upcoming', 'Timer']),
  /**
   * Determines variation of tag
   * - **custom:** The custom tag style
   * - **default:** The default tag style
   * - **icon:** The icon tag style
   * - **utility:** The utility tag style
   * - **utilityCompact:** The utility compact tag style
   * - **urgency:** The urgency tag style
   * - **value:** The value tag style
   */
  variant: _propTypes.default.oneOf(['custom', 'default', 'icon', 'utility', 'utilityCompact', 'urgency', 'value'])
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} theme='default' - sets the default variant for the theme to set the colors from DXL
 * @property {string} variant='default' - sets the default variant for the Tag
 */
const defaultProps = exports.defaultProps = {
  theme: 'default',
  variant: 'default'
};
Tag.propTypes = propTypes;
var _default = exports.default = Tag;